import { HttpHeaders } from '@angular/common/http';
import { AfterViewInit, Component, OnInit, Renderer2 } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, NavigationExtras } from '@angular/router';
import {
  ActionType,
  DeclinedOperation,
  Conversation,
  DirectMessageTemplate,
  Event,
  Member,
  Template,
  UbiquitiDeclinedOp,
} from '@quorum/models/xs-resource';
import {
  ConversationQueryParameters,
  UserQueryParameters,
  DeclinedOperationQueryParameters,
} from '@quorum/models/xs-query';
import { EventTypes } from '@models/server-common';
import { ApiService } from '@quorum/api';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { CommunicatorFacade } from '@quorum/communicator-ngrx';
import { TextMessageDialogComponent } from '@quorum/sha-components-ng-ui';
import { RouterStateService } from '@quorum/sha-router';
import { AuthenticatedUser, TokenUser } from 'libs/authentication/src/lib/+state/authentication.interfaces';
import { DropdownsStateService } from 'libs/svc-dropdowns/src/lib/+state/dropdowns-state.service';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, map, mergeMap, pairwise, startWith, switchMap, take, tap } from 'rxjs/operators';
import { ActionDeclinedOpsDialogData } from '../action-declined-ops-dialog/action-declined-ops-data.model';
import { ActionDeclinedOpDialogComponent } from '../action-declined-ops-dialog/action-declined-ops-dialog.component';
import type { Attachment } from 'libs/xsr-template-editor-ng-ui/src/lib/communicator-dialog-editor/communicator-dialog-editor.component';
@Component({
  selector: 'applications-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
})
export class ShellComponent implements OnInit, AfterViewInit {
  sidenavStatus$: Subscription;
  sidenav: boolean;
  defaultDeclinedOpsFormModel: DeclinedOperationQueryParameters;
  declinedOpsFilterForm: FormGroup;

  declinedOpsDropdowns$: Observable<any>;
  isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  searchResult$: BehaviorSubject<{ data: []; headers: null }> = new BehaviorSubject<{ data: []; headers: null }>(null);
  hasError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  searchPerformed: boolean = false;
  authenticatedUser$: Observable<AuthenticatedUser>;
  sort: string;

  constructor(
    private formBuilder: FormBuilder,
    private dropdownsStateService: DropdownsStateService,
    private apiService: ApiService,
    private route: ActivatedRoute,
    private routerStateService: RouterStateService,
    private renderer: Renderer2,
    private authenticationStateService: AuthenticationStateService,
    private dialog: MatDialog,
    private communicatorFacade: CommunicatorFacade,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit() {
    this.sort = '-dateOpened';

    this.defaultDeclinedOpsFormModel = {
      ...new DeclinedOperationQueryParameters(),
      action: '0',
      search: null,
      serviceGroupCategory: null,
      serviceGroupSubCategory: null,
      serviceAdvisorId: null,
      dateOpenedFrom: null,
      dateOpenedTo: null,
      reason: null,
      sort: '-dateOpened',
      stated: null,
      vehicleYearFrom: null,
      vehicleYearTo: null,
      workOrderId: null,
      pageSize: 25,
      page: 0,
    };

    this.declinedOpsFilterForm = this.formBuilder.group({
      action: '0',
      search: null,
      serviceGroupCategory: null,
      serviceAdvisorId: null,
      serviceGroupSubCategory: null,
      dateOpenedFrom: null,
      dateOpenedTo: new Date(new Date().setDate(new Date().getDate() - 7)),
      reason: null,
      stated: null,
      sort: '-dateOpened',
      vehicleYearFrom: [null, [Validators.pattern('^[0-9]+$'), Validators.minLength(4), Validators.maxLength(4)]],
      vehicleYearTo: [null, [Validators.pattern('^[0-9]+$'), Validators.minLength(4), Validators.maxLength(4)]],
      workOrderId: [null, [Validators.pattern('^[0-9]+$')]],
      pageSize: 25,
      page: 0,
    });

    this.authenticatedUser$ = this.authenticationStateService
      .selectAuthenticatedUser()
      .pipe(filter((user) => user != null));

    this.declinedOpsFilterForm.valueChanges
      .pipe(startWith(null), pairwise())
      .subscribe(([previousValue, currentValue]) => {
        if (previousValue && previousValue.page === currentValue.page) {
          this.sort = currentValue.sort;
          this.declinedOpsFilterForm.patchValue({ ...currentValue, page: 0 }, { emitEvent: false });
          this.updateRouter({ ...currentValue, page: 0 });
        } else {
          this.updateRouter(currentValue);
        }
      });

    this.declinedOpsDropdowns$ = combineLatest([
      this.dropdownsStateService.selectServiceGroupCategories().pipe(
        filter((serviceGroupCategories) => !this.dropdownsStateService.isEmpty(serviceGroupCategories)),
        map((serviceGroupCategories) =>
          serviceGroupCategories.filter((serviceGroupCategory: any) => serviceGroupCategory.id > 0)
        )
      ),
      this.dropdownsStateService.selectDeclinedReasons().pipe(
        filter((declinedReasons) => !this.dropdownsStateService.isEmpty(declinedReasons)),
        map((declinedReasons) => declinedReasons.filter((declinedReasons: any) => declinedReasons.id > 0))
      ),
      this.dropdownsStateService.selectTextMessageTemplates(),
      this.dropdownsStateService.selectEmployees().pipe(
        filter((employees) => !this.dropdownsStateService.isEmpty(employees)),
        map((employees) => {
          return employees.filter((emp) => emp.jobTitleId == 'SAD');
        })
      ),
      this.dropdownsStateService.selectActionTypes(),
    ]).pipe(
      take(1),
      map(([serviceGroupCategories, declinedReasons, directMessageTemplates, serviceAdvisors, actionTypes]) => {
        return { serviceGroupCategories, declinedReasons, directMessageTemplates, serviceAdvisors, actionTypes };
      })
    );
  }

  ngAfterViewInit() {
    this.route.queryParams
      .pipe(
        take(1),
        switchMap((params) => {
          if (!this.isEmpty(params)) {
            this.sort = '-dateOpened';
            this.declinedOpsFilterForm.patchValue({ ...params, stated: params.stated });
            this.isLoading$.next(true);
            return this.apiService
              .getWithHeaders<DeclinedOperation[]>(`v/1/reporting/fixed-operations/declined-operations`, {
                params: params,
              })
              .pipe(
                map((response) => {
                  this.isLoading$.next(false);

                  return { data: response.body, headers: response.headers };
                })
              );
          } else {
            this.isLoading$.next(false);
            return of({ data: null, headers: null });
          }
        })
      )
      .subscribe((sresult) => {
        this.searchResult$.next(sresult);
      });
  }

  getDeclinedOpsReportResults(formValues: any) {
    let queryParams = new DeclinedOperationQueryParameters();

    Object.keys(formValues).forEach((key) => {
      if (formValues[key] != null) {
        if (key === 'openedDateFrom' || key === 'openedDateTo') {
          queryParams[key] = new Date(formValues[key]).toISOString();
        }
        queryParams[key] = formValues[key];
      }
    });
    if (queryParams) {
      this.updateRouter(queryParams);
      this.isLoading$.next(true);
      this.hasError$.next(false);
      return this.apiService
        .getWithHeaders<DeclinedOperation[]>(`v/1/reporting/fixed-operations/declined-operations`, {
          params: queryParams,
        })
        .pipe(
          map((response) => {
            this.isLoading$.next(false);
            this.searchPerformed = true;

            return { data: response.body, headers: response.headers };
          }),
          catchError((error) => {
            this.isLoading$.next(false);
            this.hasError$.next(true);
            return of(error);
          })
        )
        .subscribe((sresult) => {
          this.searchResult$.next(sresult);
        });
    } else {
      this.isLoading$.next(false);
      return of({ data: null, headers: null });
    }
  }

  getErrorMessage(formControlName: string) {
    let errorMessage: string;
    switch (Object.keys(this.declinedOpsFilterForm.get(formControlName).errors)[0]) {
      case 'pattern':
        errorMessage = 'Must only contain numeric characters.';
        break;
      case 'minlength':
      case 'maxlength':
        errorMessage = 'Must be exactly 4 digits.';
        break;
      default:
        errorMessage = 'Invalid input';
    }
    return errorMessage;
  }

  clearResults() {
    this.searchPerformed = false;
    this.searchResult$.next({ data: [], headers: null });
  }

  pageChanged(event: any, declinedOpsFilterForm: FormGroup) {
    declinedOpsFilterForm.patchValue({
      ...declinedOpsFilterForm.value,
      page: event.pageIndex,
      pageSize: event.pageSize,
    });
    this.route.queryParams
      .pipe(
        filter((value: any) => {
          return value.page == event.pageIndex;
        }),
        take(1)
      )
      .subscribe((value) => {
        this.renderer.selectRootElement('.search-button-container button', true).click();
      });
  }

  sortChanged(sortValue: string, declinedOpsFilterForm: FormGroup) {
    declinedOpsFilterForm.patchValue({ ...declinedOpsFilterForm.value, sort: sortValue });
    this.route.queryParams
      .pipe(
        filter((value: any) => {
          return value.sort == sortValue;
        }),
        take(1)
      )
      .subscribe((value) => {
        this.renderer.selectRootElement('.search-button-container button', true).click();
      });
  }

  updateRouter(queryParams: any) {
    if (queryParams.dateOpenedFrom && typeof queryParams.dateOpenedFrom == 'object') {
      queryParams.dateOpenedFrom = new Date(queryParams.dateOpenedFrom).toISOString();
    }
    if (queryParams.dateOpenedTo && typeof queryParams.dateOpenedTo == 'object') {
      queryParams.dateOpenedTo = new Date(queryParams.dateOpenedTo).toISOString();
    }

    let navExtras: NavigationExtras = {
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    };

    this.routerStateService.go('', navExtras);
  }

  isEmpty(obj: any) {
    for (var key in obj) {
      if (obj.hasOwnProperty(key)) return false;
    }
    return true;
  }

  actionAllForCustomer(declinedOperations: DeclinedOperation[], actionTypes: ActionType[]) {
    console.log(declinedOperations);
    const dialogData: ActionDeclinedOpsDialogData = {
      actionTwoCaption: 'Action',
      actionOneCaption: 'Cancel',
      content:
        'This customer will be considered actioned and all their declined operations will be removed from the list.',
      title: 'Action Customer?',
      actionTypes: actionTypes,
    };

    const dialogRef = this.dialog.open(ActionDeclinedOpDialogComponent, {
      width: '400px',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        console.log(result);
        this.processArray(declinedOperations, result.actionTypeId);
      }
    });
  }

  async processArray(declinedOperations: DeclinedOperation[], actionTypeId: number) {
    for (const declinedOperation of declinedOperations) {
      await this.updateDeclinedOp(declinedOperation, actionTypeId);
    }
    console.log('Done!');
    this.renderer.selectRootElement('.search-button-container button', true).click();
  }

  async updateDeclinedOp(declinedOperation: DeclinedOperation, actionTypeId: number) {
    try {
      return await this.apiService
        .get<UbiquitiDeclinedOp>(`v/1/fixed-operations/ubiquiti-declined-ops/${declinedOperation.id}`)
        .pipe(
          mergeMap((declinedOp: UbiquitiDeclinedOp) => {
            declinedOp.action = true;
            declinedOp.actionDate = new Date();
            declinedOp.actionTypeId = actionTypeId;

            return this.apiService.put(`v/1/fixed-operations/ubiquiti-declined-ops/${declinedOp.id}`, declinedOp).pipe(
              tap((op: UbiquitiDeclinedOp) => {
                console.log('inside');
                console.log(op);
              })
            );
          })
        )
        .toPromise();
    } catch (err) {
      throw err;
    }
  }

  openCommunicatorTextDialog(
    directMessageTemplates: DirectMessageTemplate[],
    declinedOperation: DeclinedOperation,
    currentUser: TokenUser,
    declinedOperations: DeclinedOperation[]
  ) {
    let conversationQueryParameters: ConversationQueryParameters = new ConversationQueryParameters({
      transactionTypeId: 2,
      messageTypeId: 18,
      transactionId: declinedOperation.workOrderId,
    });

    let event: Event = new Event({
      conversationId: null,
      memberId: currentUser.id.toString(),
      eventTypeId: EventTypes.Message,
      content: 'Generating message...',
      guid: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
      sentDate: new Date(),
      sent: false,
      sendToCustomer: true,
      id: Math.floor(Math.random() * (9999999 - 999999)) + 999999,
    });

    let conversation: Conversation = new Conversation({
      dmsMessageTypeId: 18,
      dmsTransactionTypeId: 2,
      dmsTransactionId: declinedOperation.workOrderId,
      createdBy: currentUser.id,
    });

    let members: Member[] = [];
    members.push(
      new Member({
        userId: currentUser.id.toString(),
        avatarUrl: currentUser.avatarUrl,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        nickname: currentUser.firstName,
        isEmployee: true,
        isRead: true,
        isArchived: false,
        isExcluded: false,
        isPinnedToUi: false,
      })
    );

    members.push(
      new Member({
        userId: declinedOperation.associateId,
        firstName: declinedOperation.customerFirstName,
        lastName: declinedOperation.customerLastName,
        nickname: declinedOperation.customerFirstName,
        isEmployee: false,
        isRead: true,
        isArchived: false,
        isExcluded: false,
        isPinnedToUi: false,
      })
    );

    this.apiService.get(`/quorum/stores/${currentUser.storeId}`).subscribe((response) => {
      // QTemplates enabled
      if (response.useCommunicatorCloudServices) {
        this.apiService
          .get(`v/1/quorum/templates/store-gallery/${currentUser.destination}`, {
            params: { messageTypeId: 18 },
          })
          .subscribe(async (communicatorTemplates) => {
            try {
              const { CommunicatorDialogEditorComponent } = await import('@quorum/xsr-template-editor-ng-ui');
              const dialogRef = this.dialog.open(CommunicatorDialogEditorComponent, {
                data: {
                  currentUser: currentUser,
                  messageType: 'text',
                  templates: communicatorTemplates,
                },
                height: '100%',
                width: '100%',
              });

              dialogRef.afterClosed().subscribe((dialogResult) => {
                if (dialogResult && dialogResult.template) {
                  this.apiService
                    .post(`v/1/quorum/templates/item-specific/${currentUser.destination}`, dialogResult.template)
                    .subscribe((itemSpecificTemplate: Template) => {
                      event = { ...event, content: 'Generating message...' };
                      let optionalHeader = new HttpHeaders({ 'template-id': itemSpecificTemplate.id });
                      this.communicatorFacade.sendCommunicatorMessage(
                        conversationQueryParameters,
                        conversation,
                        members,
                        event,
                        optionalHeader
                      );
                      this.processArray(declinedOperations, declinedOperations[0].messagePreferenceId);
                    });
                } else if (dialogResult.messageContent) {
                  event = { ...event, content: dialogResult.messageContent };
                  let optionalHeader = new HttpHeaders({ 'Use-Default-Template': 'false' });
                  this.communicatorFacade.sendCommunicatorMessage(
                    conversationQueryParameters,
                    conversation,
                    members,
                    event,
                    optionalHeader
                  );
                  this.processArray(declinedOperations, declinedOperations[0].messagePreferenceId);
                }
              });
            } catch (err) {
              console.error('Failed to load communicator dialog module', err);
              this.snackBar.open(
                'Could not load the communicator. Please check your connection and try again.',
                'Dismiss',
                { duration: 5000 }
              );
            }
          });
      } else {
        const dialogRef = this.dialog.open(TextMessageDialogComponent, {
          width: '500px',
          height: '350px',
          data: {
            directMessageTemplates: directMessageTemplates,
          },
        });

        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            event = { ...event, content: result.textMessageBody };
            let optionalHeader = new HttpHeaders({ 'Use-Default-Template': 'false' });

            this.communicatorFacade.sendCommunicatorMessage(
              conversationQueryParameters,
              conversation,
              members,
              event,
              optionalHeader
            );
            this.processArray(declinedOperations, declinedOperations[0].messagePreferenceId);
          }
        });
      }
    });
  }

  openCommunicatorEmailDialog(
    declinedOperation: DeclinedOperation,
    currentUser: TokenUser,
    declinedOperations: DeclinedOperation[]
  ) {
    this.apiService.get(`/quorum/stores/${currentUser.storeId}`).subscribe((response) => {
      if (response.useCommunicatorCloudServices) {
        this.apiService
          .get(`v/1/quorum/templates/store-gallery/${currentUser.destination}`, {
            params: { messageTypeId: 18 },
          })
          .subscribe(async (templates) => {
            try {
              const { CommunicatorDialogEditorComponent } = await import('@quorum/xsr-template-editor-ng-ui');
              const dialogRef = this.dialog.open(CommunicatorDialogEditorComponent, {
                data: { currentUser: currentUser, messageType: 'email', templates: templates },
                height: '100%',
                width: '100%',
              });

              dialogRef.afterClosed().subscribe((dialogResult) => {
                if (dialogResult) {
                  this.apiService
                    .post(`v/1/quorum/templates/item-specific/${currentUser.destination}`, dialogResult.template)
                    .subscribe((itemSpecificTemplate: Template) => {
                      let conversationQueryParameters: ConversationQueryParameters = new ConversationQueryParameters({
                        transactionTypeId: 10,
                        messageTypeId: 18,
                        transactionId: declinedOperation.associateId,
                      });

                      let event: Event = new Event({
                        attachments: dialogResult.attachments
                          ? dialogResult.attachments.map((a: Attachment) => a.url).join(',')
                          : null,
                        conversationId: null,
                        memberId: currentUser.id.toString(),
                        eventTypeId: EventTypes.Message,
                        content: 'Generating email...',
                        guid: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
                        sentDate: new Date(),
                        sent: false,
                        sendToCustomer: true,
                        id: Math.floor(Math.random() * (9999999 - 999999)) + 999999,
                      });

                      let conversation: Conversation = new Conversation({
                        dmsMessageTypeId: 18,
                        dmsTransactionTypeId: 10,
                        dmsTransactionId: declinedOperation.associateId,
                        createdBy: currentUser.id,
                      });

                      let members: Member[] = [];
                      members.push(
                        new Member({
                          userId: currentUser.id.toString(),
                          avatarUrl: currentUser.avatarUrl,
                          firstName: currentUser.firstName,
                          lastName: currentUser.lastName,
                          nickname: currentUser.firstName,
                          isEmployee: true,
                          isRead: true,
                          isArchived: false,
                          isExcluded: false,
                          isPinnedToUi: false,
                        })
                      );

                      members.push(
                        new Member({
                          userId: declinedOperation.associateId,
                          firstName: declinedOperation.customerFirstName,
                          lastName: declinedOperation.customerLastName,
                          nickname: declinedOperation.customerFirstName,
                          isEmployee: false,
                          isRead: true,
                          isArchived: false,
                          isExcluded: false,
                          isPinnedToUi: false,
                        })
                      );

                      let optionalHeader = new HttpHeaders({ 'template-id': itemSpecificTemplate.id });

                      this.communicatorFacade.sendCommunicatorMessage(
                        conversationQueryParameters,
                        conversation,
                        members,
                        event,
                        optionalHeader
                      );

                      this.processArray(declinedOperations, declinedOperations[0].messagePreferenceId);
                    });
                }
              });
            } catch (err) {
              console.error('Failed to load communicator dialog module', err);
              this.snackBar.open(
                'Could not load the communicator. Please check your connection and try again.',
                'Dismiss',
                { duration: 5000 }
              );
            }
          });
      }
    });
  }
}
